/**
 * Feed-derived workspace todos with stable ordering and bucket-scoped review state.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { useUser } from "@/auth/UserContext";
import { useFeed } from "@/context/FeedContext";
import {
  buildFeedTodoCandidates,
  FEED_TODO_LIMIT,
  FEED_TODO_REVIEWED_REMOVE_MS,
  type FeedTodoBucket,
  type FeedTodoCandidate,
} from "@/features/workspace/feedTodos";
import type { WorkspaceTodo } from "@/types/workspace";

/** Reviewed snapshot kept until the bucket gets a newer feed event (or timed out at cap). */
interface ReviewedSnapshot {
  eventId: string;
  label: string;
  href: string;
  /** Original event time — keeps sort position when marked done. */
  createdAt: string;
}

function candidateToWorkspaceTodo(
  c: FeedTodoCandidate,
  done: boolean,
): WorkspaceTodo {
  return {
    id: c.bucket,
    label: c.label,
    href: c.href,
    done,
    feedEventId: c.eventId,
  };
}

function snapshotToTodo(bucket: FeedTodoBucket, s: ReviewedSnapshot): WorkspaceTodo {
  return {
    id: bucket,
    label: s.label,
    href: s.href,
    done: true,
    feedEventId: s.eventId,
  };
}

function countDisplaySlots(
  candidates: FeedTodoCandidate[],
  reviewed: Map<FeedTodoBucket, ReviewedSnapshot>,
): number {
  const buckets = new Set<FeedTodoBucket>();
  for (const c of candidates) buckets.add(c.bucket);
  for (const bucket of reviewed.keys()) buckets.add(bucket);
  return buckets.size;
}

/**
 * Reviewed todos stay in the list (strikethrough, same slot order) until a new
 * feed event replaces that bucket. When the list is full ({@link FEED_TODO_LIMIT}),
 * a reviewed row is removed after {@link FEED_TODO_REVIEWED_REMOVE_MS}.
 */
export function useFeedTodos(): {
  todos: WorkspaceTodo[];
  loading: boolean;
} {
  const { events, loading: feedLoading } = useFeed();
  const { isClient } = useUser();
  const candidates = useMemo(
    () => buildFeedTodoCandidates(events, { isClient }),
    [events, isClient],
  );

  const [reviewedByBucket, setReviewedByBucket] = useState<
    Map<FeedTodoBucket, ReviewedSnapshot>
  >(() => new Map());
  const prevCandidatesRef = useRef<FeedTodoCandidate[]>([]);
  const removeTimersRef = useRef<Map<FeedTodoBucket, number>>(new Map());
  const reviewedByBucketRef = useRef(reviewedByBucket);
  reviewedByBucketRef.current = reviewedByBucket;

  useEffect(() => {
    return () => {
      removeTimersRef.current.forEach((id) => window.clearTimeout(id));
      removeTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const nextByBucket = new Map(candidates.map((c) => [c.bucket, c]));
    setReviewedByBucket((current) => {
      let changed = false;
      const next = new Map(current);
      for (const [bucket, snapshot] of current) {
        const candidate = nextByBucket.get(bucket);
        if (candidate != null && candidate.eventId !== snapshot.eventId) {
          next.delete(bucket);
          changed = true;
          const timerId = removeTimersRef.current.get(bucket);
          if (timerId != null) {
            window.clearTimeout(timerId);
            removeTimersRef.current.delete(bucket);
          }
        }
      }
      return changed ? next : current;
    });
  }, [candidates]);

  useEffect(() => {
    const prev = prevCandidatesRef.current;
    prevCandidatesRef.current = candidates;

    const prevByBucket = new Map(prev.map((c) => [c.bucket, c]));
    const nextByBucket = new Map(candidates.map((c) => [c.bucket, c]));
    const newlyReviewed: Array<{
      bucket: FeedTodoBucket;
      source: FeedTodoCandidate;
    }> = [];

    for (const candidate of candidates) {
      const was = prevByBucket.get(candidate.bucket);
      if (was != null && was.eventId !== candidate.eventId) continue;

      const eventPresent = events.some((e) => e.id === candidate.eventId);
      const becameReviewed =
        (candidate.isReviewed && was != null && !was.isReviewed) ||
        (was != null && !eventPresent);

      if (becameReviewed && !reviewedByBucketRef.current.has(candidate.bucket)) {
        newlyReviewed.push({ bucket: candidate.bucket, source: was ?? candidate });
      }
    }

    for (const was of prev) {
      if (nextByBucket.has(was.bucket)) continue;
      if (
        !events.some((e) => e.id === was.eventId) &&
        !reviewedByBucketRef.current.has(was.bucket)
      ) {
        newlyReviewed.push({ bucket: was.bucket, source: was });
      }
    }

    if (newlyReviewed.length === 0) return;

    setReviewedByBucket((current) => {
      const next = new Map(current);
      for (const { bucket, source } of newlyReviewed) {
        next.set(bucket, {
          eventId: source.eventId,
          label: source.label,
          href: source.href,
          createdAt: source.createdAt,
        });
      }
      return next;
    });

    for (const { bucket, source } of newlyReviewed) {
      const reviewedAfter = new Map(reviewedByBucketRef.current);
      reviewedAfter.set(bucket, {
        eventId: source.eventId,
        label: source.label,
        href: source.href,
        createdAt: source.createdAt,
      });
      if (
        countDisplaySlots(candidates, reviewedAfter) >= FEED_TODO_LIMIT &&
        !removeTimersRef.current.has(bucket)
      ) {
        const timerId = window.setTimeout(() => {
          removeTimersRef.current.delete(bucket);
          setReviewedByBucket((m) => {
            if (!m.has(bucket)) return m;
            const copy = new Map(m);
            copy.delete(bucket);
            return copy;
          });
        }, FEED_TODO_REVIEWED_REMOVE_MS);
        removeTimersRef.current.set(bucket, timerId);
      }
    }
  }, [candidates, events]);

  const todos = useMemo(() => {
    const rows: { createdAt: string; todo: WorkspaceTodo }[] = [];

    for (const candidate of candidates) {
      const reviewed = reviewedByBucket.get(candidate.bucket);
      if (reviewed != null && reviewed.eventId === candidate.eventId) {
        rows.push({
          createdAt: reviewed.createdAt,
          todo: snapshotToTodo(candidate.bucket, reviewed),
        });
        continue;
      }
      const eventPresent = events.some((e) => e.id === candidate.eventId);
      if (eventPresent && !candidate.isReviewed) {
        rows.push({
          createdAt: candidate.createdAt,
          todo: candidateToWorkspaceTodo(candidate, false),
        });
      }
    }

    for (const [bucket, reviewed] of reviewedByBucket) {
      if (candidates.some((c) => c.bucket === bucket)) continue;
      rows.push({
        createdAt: reviewed.createdAt,
        todo: snapshotToTodo(bucket, reviewed),
      });
    }

    return rows
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      .slice(0, FEED_TODO_LIMIT)
      .map((r) => r.todo);
  }, [candidates, events, reviewedByBucket]);

  return { todos, loading: feedLoading };
}
